import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Lock } from "lucide-react";
import { api, errMsg, eur } from "@/lib/api";
import { useApi, Btn, Field } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";

const INCLUDED = ["Les 28 modules LBC Agent, à vie", "Command Center et 3 niveaux d'autonomie", "Early Morning Debrief par email & Telegram", "Anti-arnaque, prix plancher protégé, audit complet", "Extension Chrome + import CSV", "Mises à jour incluses"];

export default function Order() {
  const { user } = useAuth();
  const { data: offer } = useApi("/payments/offer");
  const [f, setF] = useState({ email: "", first_name: "" });
  const [p] = useSearchParams();
  const cartTok = p.get("cart");
  useEffect(() => {
    if (!cartTok) return;
    api.get(`/payments/cart/${cartTok}`).then(({ data }) => { setF({ email: data.email, first_name: data.first_name }); if (data.ref && !localStorage.getItem("lbc_ref")) localStorage.setItem("lbc_ref", data.ref); }).catch(() => {});
  }, [cartTok]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const active = user && (user.role === "admin" || !["PENDING", "EXPIRED", "SUSPENDED"].includes(user.entitlement?.status));
  if (active) return <Navigate to="/" replace />;
  const pay = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = { origin_url: window.location.origin };
      const { data } = user ? await api.post("/payments/checkout", body) : await api.post("/payments/guest-checkout", { ...body, ...f, ref: localStorage.getItem("lbc_ref") || undefined });
      window.location.href = data.url;
    } catch (x) { setErr(errMsg(x)); setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-black grid-bg px-4 py-10" data-testid="order-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/offre" className="font-display font-black text-xl">LBC<span className="text-[#7CFF00]"> Agent</span></Link>
        <div className="grid lg:grid-cols-2 gap-8 mt-10">
          <div className="card glow p-8">
            <div className="mono text-xs tracking-[.3em] text-[#FFBD36]">ACCÈS À VIE · PAIEMENT UNIQUE</div>
            <div className="text-6xl font-black mt-4" data-testid="order-price">{offer ? eur(offer.price) : "…"}</div>
            <p className="text-sm text-[#A0AAB2] mt-2">Une seule fois. Pas d'abonnement, pas de frais cachés.</p>
            <ul className="mt-8 space-y-3">{INCLUDED.map((t) => <li key={t} className="flex gap-2 text-sm"><Check size={16} className="text-[#7CFF00] shrink-0 mt-0.5" />{t}</li>)}</ul>
            <p className="text-xs text-[#626E77] mt-8">Garantie échange Profimy™ · 4,6/5 sur Trustpilot (144 avis).</p>
          </div>
          <form onSubmit={pay} className="card p-8 space-y-4" data-testid="order-form">
            <h1 className="text-2xl font-black">Finaliser ma commande</h1>
            {user ? <p className="text-sm text-[#A0AAB2]">Connecté en tant que <b className="text-white">{user.email}</b>.</p> : <>
              <p className="text-sm text-[#A0AAB2]">Pas besoin de créer un compte : il sera créé automatiquement après le paiement.</p>
              <Field label="Prénom"><input className="inp" required value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="order-first-name" /></Field>
              <Field label="Email (ton identifiant)"><input className="inp" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="order-email" /></Field></>}
            {err && <div className="text-[#FF453A] text-sm" data-testid="order-error">{err}</div>}
            <Btn className="w-full py-3 text-base" disabled={busy} data-testid="order-pay-btn"><Lock size={15} />{busy ? "Redirection…" : `Payer ${offer ? eur(offer.price) : ""} en toute sécurité`}</Btn>
            <p className="text-xs text-[#626E77] text-center">Paiement sécurisé par Stripe. Tu choisiras ton mot de passe juste après.</p>
            {!user && <p className="text-xs text-center text-[#A0AAB2]">Déjà un compte ? <Link to="/login" className="text-[#7CFF00]">Connexion</Link></p>}
          </form>
        </div>
      </div>
    </div>
  );
}

export function SetPasswordForm({ token, email }) {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setErr("Les mots de passe ne correspondent pas.");
    try { await api.post("/auth/set-password", { token, password: pw.a }); await refresh(); nav("/onboarding"); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 text-left" data-testid="set-password-form">
      {email && <p className="text-sm text-[#A0AAB2]">Identifiant : <b className="text-white">{email}</b></p>}
      <Field label="Choisis ton mot de passe (8 caractères min.)"><input type="password" minLength={8} required className="inp" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="set-password-input" /></Field>
      <Field label="Confirme-le"><input type="password" minLength={8} required className="inp" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="set-password-confirm" /></Field>
      {err && <div className="text-[#FF453A] text-sm" data-testid="set-password-error">{err}</div>}
      <Btn className="w-full" data-testid="set-password-submit">Valider et entrer dans LBC Agent</Btn>
    </form>
  );
}

export function SetPasswordPage() {
  const [p] = useSearchParams();
  return (
    <div className="min-h-screen bg-black grid-bg grid place-items-center p-4" data-testid="set-password-page">
      <div className="card p-8 max-w-md w-full"><h1 className="text-2xl font-black mb-5">Choisis ton mot de passe</h1><SetPasswordForm token={p.get("token")} /></div>
    </div>
  );
}
